import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import {
  getCategoryListingPaginate,
  getManufacturesListingPaginate,
} from '../../api/context/manufacturer';
import { getBrandListing } from '../../api/context/brand';

const CreateUpdateAutoNotificationModal = ({
  show,
  onHide,
  notification,
  onSave,
  onCancel,
  onFieldChange,
  saving = false
}) => {
  const [navigationType, setNavigationType] = useState('');
  const [navigationData, setNavigationData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const navigationTypes = [
    // { value: 'Homepage', label: 'Homepage' },
    { value: 'Brand', label: 'Brand' },
    { value: 'Vendor', label: 'Vendor' },
    { value: 'Store', label: 'Store' }, // 🆕 Added Store option
    // { value: 'Category', label: 'Category' },
  ];

  useEffect(() => {
    const navigateTo = notification?.navigateToPage;
    if (!navigateTo) {
      setNavigationType('');
      setSelectedData(null);
      setNavigationData([]);
      return;
    }

    const [type, name = ''] = navigateTo.split('/');
    if (type === navigationType) return;

    setNavigationType(type);
    if (type && type !== 'Homepage') {
      loadNavigationData(type, name);
    } else if (name) {
      setSelectedData({ value: name, label: name });
    } else {
      setSelectedData(null);
    }
  }, [notification?.navigateToPage, show]);

  // ✅ Load dropdown data based on type
  const loadNavigationData = async (type, existingValue = '') => {
    setLoadingData(true);
    setNavigationData([]);
    setSelectedData(null);

    try {
      const updateSelection = (list) => {
        setNavigationData(list);
        if (existingValue) {
          const found = list.find(item => item.value === existingValue);
          if (found) setSelectedData(found);
        }
        setLoadingData(false);
      };

      switch (type) {
        case 'Brand':
          getBrandListing({ page: 1, pageSize: 100, search: '' })((response) => {
            if (response?.statusCode === 200) {
              const brands = response?.data?.map(brand => ({
                value: brand.name,
                label: brand.name,
                id: brand.id,
              }));
              updateSelection(brands);
            } else setLoadingData(false);
          });
          break;

        case 'Vendor':
        case 'Store': // 🆕 Reuse same API logic for Store
          getManufacturesListingPaginate({ page: 1, limit: 100, search: '' })((response) => {
            if (response?.statusCode === 200) {
              const vendors = response?.results?.map(vendor => ({
                value: vendor.label,
                label: vendor.label,
                id: vendor.value,
              }));
              updateSelection(vendors);
            } else setLoadingData(false);
          });
          break;

        case 'Category':
          getCategoryListingPaginate({ page: 1, limit: 100, search: '' })((response) => {
            if (response?.statusCode === 200) {
              const categories = response?.results?.map(cat => ({
                value: cat.label,
                label: cat.label,
                id: cat.id,
              }));
              updateSelection(categories);
            } else setLoadingData(false);
          });
          break;

        default:
          setLoadingData(false);
          break;
      }
    } catch (error) {
      console.error('Error loading navigation data:', error);
      setLoadingData(false);
    }
  };

  // ✅ Handle navigation change
  const handleNavigationTypeChange = (selectedOption) => {
    const type = selectedOption?.value || '';
    setNavigationType(type);
    setSelectedData(null);

    if (onFieldChange) {
      onFieldChange('navigationType', type);
      onFieldChange('navigationId', null);
      onFieldChange('navigateToPage', type);
    }

    if (type && type !== 'Homepage') {
      loadNavigationData(type);
    }
  };

  // ✅ Handle target item change
  const handleDataChange = (selectedOption) => {
    setSelectedData(selectedOption);

    if (selectedOption && navigationType) {
      const navigateToPage = `${navigationType}/${selectedOption.value}`;
      if (onFieldChange) {
        onFieldChange('navigateToPage', navigateToPage);
        onFieldChange('navigationType', navigationType);
        onFieldChange('navigationId', selectedOption.id || null);
      }
    } else if (navigationType === 'Homepage') {
      if (onFieldChange) {
        onFieldChange('navigateToPage', 'Homepage');
        onFieldChange('navigationType', 'Homepage');
        onFieldChange('navigationId', null);
      }
    } else {
      if (onFieldChange) {
        onFieldChange('navigateToPage', '');
        onFieldChange('navigationId', null);
      }
    }
  };

  const getDataPlaceholder = () => {
    if (loadingData) return 'Loading...';
    switch (navigationType) {
      case 'Brand': return 'Select brand';
      case 'Vendor': return 'Select vendor';
      case 'Store': return 'Select store'; // 🆕 Added Store label
      case 'Category': return 'Select category';
      case 'Homepage': return 'No selection needed';
      default: return 'Select type first';
    }
  };

  const isDataSelectionRequired = () =>
    navigationType && navigationType !== 'Homepage';

  const isEditable = !notification?.id ||
    (notification?.status?.toLowerCase() === 'upcoming' ||
      notification?.status?.toLowerCase() === 'scheduled');

  const handleFieldChange = (field, value) => {
    if (onFieldChange) onFieldChange(field, value);
  };

  const getModalTitle = () =>
    !notification?.id ? 'Schedule New Notification' : 'Notification Details';

  const getSaveButtonText = () => {
    if (saving) return !notification?.id ? 'Scheduling...' : 'Updating...';
    return !notification?.id ? 'Schedule Notification' : 'Update Notification';
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 36,
      fontSize: 12,
      borderRadius: 6,
      borderColor: state.isFocused ? '#008695' : '#cbd5e1',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 134, 149, 0.1)' : 'none',
      '&:hover': { borderColor: state.isFocused ? '#008695' : '#94a3b8' },
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 10px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
    placeholder: (base) => ({ ...base, fontSize: 12, color: '#94a3b8' }),
    singleValue: (base) => ({ ...base, fontSize: 12, color: '#0f172a' }),
    indicatorsContainer: (base) => ({ ...base, height: 34 }),
    dropdownIndicator: (base) => ({ ...base, padding: 6, color: '#64748b' }),
    clearIndicator: (base) => ({ ...base, padding: 6 }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: '#e2e8f0' }),
    menu: (base) => ({ ...base, fontSize: 12, borderRadius: 8, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)', border: '1px solid #e2e8f0' }),
    option: (base, state) => ({
      ...base,
      fontSize: 12,
      padding: '8px 12px',
      background: state.isSelected ? '#008695' : state.isFocused ? '#f0fdfa' : '#fff',
      color: state.isSelected ? '#fff' : '#0f172a',
      cursor: 'pointer',
    }),
  };

  return (
    <Modal show={show} onHide={onHide} scrollable centered size="md" dialogClassName="an-modal">
      <style>{`
        .an-modal .modal-dialog { max-width: 560px; }
        .an-modal .modal-content { border: 0; border-radius: 12px; overflow: hidden; }
        .an-modal .modal-header { padding: 14px 20px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; }
        .an-modal .modal-header .modal-title { margin: 0 !important; padding: 0 !important; display: inline-flex; align-items: center; }
        .an-modal .modal-header .btn-close { margin: 0 !important; padding: 8px !important; font-size: 12px; align-self: center; }
        .an-modal .an-modal-title { margin: 0; padding: 0; font-size: 14px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; line-height: 1.2; }
        .an-modal .modal-body { padding: 16px 20px 20px; max-height: 70vh; }
        .an-modal .modal-footer { padding: 12px 20px; border-top: 1px solid #e5e7eb; background: #f8fafc; }
        .an-modal .form-label { margin-bottom: 4px; font-size: 11px; font-weight: 600; color: #475569; letter-spacing: 0.01em; }
        .an-modal .form-label .req { color: #dc2626; margin-left: 2px; }
        .an-modal .form-control {
          font-size: 12px;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid #cbd5e1;
          color: #0f172a;
          min-height: 36px;
        }
        .an-modal textarea.form-control { min-height: 68px; resize: vertical; }
        .an-modal .form-control:focus {
          border-color: #008695;
          box-shadow: 0 0 0 3px rgba(0, 134, 149, 0.1);
        }
        .an-modal .form-control:disabled { background: #f8fafc; color: #64748b; }
        .an-modal .an-modal-row { margin: 0 -6px 12px; display: flex; flex-wrap: wrap; }
        .an-modal .an-modal-row > .an-col { padding: 0 6px; flex: 0 0 50%; max-width: 50%; }
        .an-modal .an-modal-group { margin-bottom: 12px; }
        .an-modal .an-modal-group:last-child { margin-bottom: 0; }
        .an-modal .an-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 0 16px;
          height: 34px;
          border-radius: 6px;
          border: 1px solid transparent;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          min-width: 90px;
        }
        .an-modal .an-btn-cancel { background: #fff; border-color: #e2e8f0; color: #475569; }
        .an-modal .an-btn-cancel:hover:not(:disabled) { border-color: #cbd5e1; background: #f8fafc; }
        .an-modal .an-btn-primary { background: #008695; color: #fff; }
        .an-modal .an-btn-primary:hover:not(:disabled) { background: #007784; }
        .an-modal .an-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        @media (max-width: 575.98px) {
          .an-modal .modal-dialog { margin: 8px; }
          .an-modal .modal-header { padding: 12px 16px; }
          .an-modal .modal-body { padding: 14px 16px 16px; }
          .an-modal .modal-footer { padding: 10px 16px; }
          .an-modal .an-modal-row { margin: 0 -4px 10px; }
          .an-modal .an-modal-row > .an-col { padding: 0 4px; flex: 0 0 50%; max-width: 50%; }
          .an-modal .an-modal-title { font-size: 13px; }
          .an-modal .an-btn { min-width: 0; flex: 1; padding: 0 12px; font-size: 11.5px; }
        }
      `}</style>

      <Modal.Header closeButton>
        <Modal.Title as="span" className="an-modal-title">{getModalTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {notification && (
          <div className="notification-form">
            <Form.Group className="an-modal-group">
              <Form.Label>Title<span className="req">*</span></Form.Label>
              <Form.Control
                type="text"
                value={notification.title || ''}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                placeholder="Enter notification title"
                disabled={saving || !isEditable}
              />
            </Form.Group>

            <Form.Group className="an-modal-group">
              <Form.Label>Message<span className="req">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={notification.message || ''}
                onChange={(e) => handleFieldChange('message', e.target.value)}
                placeholder="Enter notification message"
                disabled={saving || !isEditable}
              />
            </Form.Group>

            <div className="an-modal-row">
              <div className="an-col">
                <Form.Label>Navigation</Form.Label>
                <Select
                  options={navigationTypes}
                  value={navigationTypes.find(opt => opt.value === navigationType) || null}
                  onChange={handleNavigationTypeChange}
                  placeholder="Select"
                  isDisabled={saving || !isEditable}
                  isLoading={loadingData}
                  styles={selectStyles}
                />
              </div>
              <div className="an-col">
                <Form.Label>Target</Form.Label>
                <Select
                  options={navigationData}
                  value={selectedData}
                  onChange={handleDataChange}
                  placeholder={getDataPlaceholder()}
                  isDisabled={!isDataSelectionRequired() || saving || !isEditable}
                  isLoading={loadingData}
                  isSearchable
                  styles={selectStyles}
                />
              </div>
            </div>

            <div className="an-modal-row" style={{ marginBottom: 0 }}>
              <div className="an-col">
                <Form.Label>Date<span className="req">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={notification.scheduledDate || ''}
                  onChange={(e) => handleFieldChange('scheduledDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  disabled={saving || !isEditable}
                />
              </div>
              <div className="an-col">
                <Form.Label>Time<span className="req">*</span></Form.Label>
                <Form.Control
                  type="time"
                  value={notification.scheduledTime || ''}
                  onChange={(e) => handleFieldChange('scheduledTime', e.target.value)}
                  disabled={saving || !isEditable}
                />
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="an-btn an-btn-cancel" onClick={onCancel} disabled={saving}>
          {isEditable ? 'Cancel' : 'Close'}
        </button>
        {isEditable && (
          <button type="button" className="an-btn an-btn-primary" onClick={onSave} disabled={saving}>
            {getSaveButtonText()}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateUpdateAutoNotificationModal;
